import { type JSX, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Icon } from '@/components/icons';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';

/**
 * Topbar avatar pill + dropdown menu. Replaces the single-click logout
 * button that lived here before — that one fired logout on first click,
 * which was a footgun: any accidental tap signed the operator out.
 *
 * Items shown in the dropdown:
 *   - Account header (name + email)
 *   - Settings link (workspace settings — already exists at /settings)
 *   - Logout
 *
 * Theme + language toggles stay as separate icon buttons in the topbar
 * (they're frequent toggles, one click vs two). Switch-workspace stays
 * in the sidebar tenant switcher (already prominent there). This menu
 * is the home for everything that doesn't justify its own topbar slot.
 *
 * Closes on outside click, Escape, or after any item is clicked.
 */
export function UserMenu(): JSX.Element {
  const { t } = useTranslation('default');
  const { user, logout, isLoggingOut } = useAuth();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const displayName = user?.name ?? '';
  const email = user?.email ?? '';
  const initial = user?.initial ?? '?';

  // Close on outside click. mousedown so clicking the trigger doesn't
  // immediately re-toggle (the trigger handler runs after this fires).
  useEffect(() => {
    if (!open) return;
    const onPointer = (e: MouseEvent): void => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const handleLogout = (): void => {
    setOpen(false);
    void logout();
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={isLoggingOut}
        className={cn(
          'flex items-center gap-2 pl-2.5 pr-1 py-1 bg-transparent border border-transparent rounded-md text-[13px] text-text disabled:opacity-50',
          'hover:bg-surface-3',
          open && 'bg-surface-3',
        )}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('um_open')}
      >
        <span className="text-[13px] text-text-2">{displayName}</span>
        <span
          aria-hidden="true"
          className="w-7 h-7 rounded-full grid place-items-center text-volt-ink text-[12px] font-bold"
          style={{
            background: 'linear-gradient(135deg, var(--volt) 0%, var(--volt-600) 100%)',
          }}
        >
          {initial}
        </span>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-1.5 w-[260px] bg-surface border border-line rounded-lg shadow-lg overflow-hidden z-50"
        >
          {/* Header — name + email so the operator confirms which
              account they're acting as before clicking logout. */}
          <div className="px-4 py-3 border-b border-line">
            <div className="text-[13px] text-text font-semibold leading-tight">{displayName}</div>
            {email && (
              <div className="mono text-[11.5px] text-text-3 mt-0.5 truncate">{email}</div>
            )}
          </div>

          <div className="py-1">
            <MenuItem
              to="/settings"
              icon={<Icon.cog width="14" height="14" />}
              label={t('um_settings')}
              onClick={() => setOpen(false)}
            />
          </div>

          <div className="py-1 border-t border-line-soft">
            <button
              type="button"
              role="menuitem"
              onClick={handleLogout}
              disabled={isLoggingOut}
              className="w-full flex items-center gap-2.5 px-4 py-2 text-[13px] text-danger hover:bg-danger-bg disabled:opacity-50 text-left"
            >
              <Icon.arrowRight width="14" height="14" />
              <span>{isLoggingOut ? t('um_logging_out') : t('um_logout')}</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

interface MenuItemProps {
  to: string;
  icon: JSX.Element;
  label: string;
  onClick: () => void;
}
function MenuItem({ to, icon, label, onClick }: MenuItemProps): JSX.Element {
  return (
    <Link
      role="menuitem"
      to={to}
      onClick={onClick}
      className="flex items-center gap-2.5 px-4 py-2 text-[13px] text-text hover:bg-surface-2"
    >
      <span className="text-text-3">{icon}</span>
      <span>{label}</span>
    </Link>
  );
}
